import * as Popover from "@radix-ui/react-popover";
import MapboxClient from "mapbox";
import React, {
  type FC,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import Input from "../inputs/Input";
import { geolocateControl } from "../map/hooks/useGeolocateControl";
import type { GeocoderProps } from "./Geocoder.types";
import Results from "./Results";

const Geocoder: FC<GeocoderProps> = ({
  statefulMap,
  formik,
  variant,
  bbox,
  callback,
  value,
  extraOptions = true,
  types = "country,region,postcode,district,place,locality,neighborhood,address,poi",
  proximity,
  placeholder,
  onFocus,
  onBlur,
  useTriggerWidth,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [searchTerm, setSearchTerm] = useState(value || "");
  const [geolocateLoading, setGeolocateLoading] = useState(false);
  const [geocoder, setGeocoder] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);

  useEffect(() => {
    const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    const mapboxGeocoder = new MapboxClient(accessToken);
    setGeocoder(mapboxGeocoder);
  }, []);

  useEffect(() => {
    if (value) {
      setSearchTerm(value);
    }
  }, [value]);

  useEffect(() => {
    let timeout;
    if (searchTerm.length >= 3) {
      timeout = setTimeout(() => {
        geocoder
          .geocodeForward(searchTerm, {
            limit: 5,
            autocomplete: true,
            fuzzyMatch: true,
            language: "de",
            bbox,
            types,
            proximity,
          })
          .then((res) => {
            setResults(res.entity.features);
          })
          .catch((err) => {
            throw new Error(`${err} error in Geocoder.tsx`);
          });
      }, 200);
    } else {
      setResults([]);
    }

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const onSelected = (item) => {
    callback?.(item);
    setSearchTerm(item.place_name);

    let zoomLevel;
    switch (item.place_type[0]) {
      case "country":
        zoomLevel = 5;
        break;
      case "region":
        zoomLevel = 9;
        break;
      case "district":
        zoomLevel = 10;
        break;
      case "place":
      case "locality":
      case "neighborhood":
        zoomLevel = 12;
        break;
      case "address":
      case "poi":
        zoomLevel = 16.5;
        break;
      default:
        zoomLevel = 12;
        break;
    }

    statefulMap?.flyTo({
      center: [item.center[0], item.center[1]],
      zoom: zoomLevel,
      duration: 2700,
      pitch: 30,
    });

    setShowResults(false);
  };

  const handleGeolocate = () => {
    setGeolocateLoading(true);
    geolocateControl(statefulMap).then(() => {
      setGeolocateLoading(false);
      setShowResults(false);
    });
  };

  const handleInputKeyDown = useCallback(
    (event) => {
      const totalItems = results.length + (extraOptions ? 2 : 0);

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveResultIndex((prevIndex) =>
          prevIndex < totalItems - 1 ? prevIndex + 1 : prevIndex,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveResultIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : -1,
        );
      } else if (event.key === "Enter") {
        if (activeResultIndex >= 0 && activeResultIndex < results.length) {
          event.preventDefault();
          onSelected(results[activeResultIndex]);
        } else if (extraOptions) {
          const optionIndex = activeResultIndex - results.length;
          if (optionIndex === 0) {
            handleGeolocate();
          } else if (optionIndex === 1) {
            // setShowResults(false);
          }
        }
      } else if (event.key === "Escape") {
        setShowResults(false);
      }
    },
    [results, activeResultIndex, onSelected, extraOptions, handleGeolocate],
  );

  const handleInputChange = (event) => {
    setSearchTerm(event.target.value);
    setActiveResultIndex(-1);
  };

  const handleClickOutside = (event) => {
    if (
      popoverRef.current &&
      !popoverRef.current?.contains(event.target) &&
      !inputRef.current?.contains(event.target)
    ) {
      setShowResults(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={popoverRef} className="w-full">
      <Popover.Root open={showResults}>
        <Popover.Trigger className="w-full">
          <div style={{ width: "100%", position: "relative" }}>
            <Input
              ref={inputRef}
              name="searchAddress"
              type="text"
              variant={variant}
              leadingIcon={"pin"}
              placeholder={
                placeholder || t("util:input", { val: t("global:address") })
              }
              trailingIcon={searchTerm.length > 0 ? "x-md" : undefined}
              trailingIconClick={
                searchTerm.length > 0
                  ? () => {
                      setSearchTerm("");
                      setResults([]);
                      formik?.setFieldValue("address", "");
                      setActiveResultIndex(-1);
                    }
                  : undefined
              }
              onChange={handleInputChange}
              onFocus={() => {
                onFocus?.();
                setShowResults(true);
              }}
              onBlur={(e) => {
                onBlur?.(e);
                if (
                  popoverRef.current &&
                  !popoverRef.current?.contains(e.relatedTarget) &&
                  !inputRef.current?.contains(e.relatedTarget)
                ) {
                  setTimeout(() => {
                    setShowResults(false);
                  }, 500);
                }
              }}
              onKeyDown={handleInputKeyDown}
              value={searchTerm}
            />
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className="z-[99999999999] mt-2 rounded-2xl border shadow-xl"
            sideOffset={5}
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onMouseDown={(e) => e.stopPropagation()} // Stop propagation for clicks inside Popover
          >
            {(results.length > 0 || extraOptions) && (
              <Results
                results={results}
                onSelected={(item) => {
                  onSelected(item);
                }}
                setShowResults={setShowResults}
                handleGeolocate={handleGeolocate}
                geolocateLoading={geolocateLoading}
                extraOptions={extraOptions}
                className={isMobile ? "w-[calc(100vw_+_20px)]" : "w-[360px]"}
                activeResultIndex={activeResultIndex}
              />
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

export default Geocoder;
